import { useEffect, useRef, useState } from 'react';
import { useButton } from 'react-aria';
import {
  PeriodicityTypes,
  usePatchCustomerPeriodicities,
  useProductPeriodicityMap,
} from '@hungryroot/components-osn';
import { ButtonsGroup } from '@hungryroot/pho';
import { type Tag } from '@hungryroot/rest';
import { useIdentify } from '@hungryroot/rest-hooks';
import { Heading, Frequency } from '@hungryroot/ui-kit';
import { useFoodProfileNeverOverride } from '../../hooks/useFoodProfileNeverOverride';
import { FoodProfileNeverOverrideAlert } from './FoodProfileNeverOverrideAlert';
import styles from './OSNSection.module.css';

type OSNSectionProps = {
  productId: number;
  productTags?: Tag[] | number[];
  enableFoodProfileNeverOverride?: boolean;
};

export function OSNSection(props: OSNSectionProps) {
  const {
    productId,
    productTags,
    enableFoodProfileNeverOverride = false,
  } = props;

  const { showFoodProfileNeverOverride, neverTagName } =
    useFoodProfileNeverOverride({
      productTags,
      enabled: enableFoodProfileNeverOverride,
    });

  return (
    <div className={styles['osn-section']}>
      <Heading className={styles.heading} variant="H6">
        How often should we send this?
      </Heading>
      {showFoodProfileNeverOverride ? (
        <FoodProfileNeverOverrideAlert tagName={neverTagName} />
      ) : (
        <OSNButtonGroup productId={productId} />
      )}
    </div>
  );
}

type OSNButtonGroupProps = {
  productId: number;
  onChange?: ({ option }: { option: PeriodicityTypes | null }) => void;
  className?: string;
};

const periodicityOptions: { label: string; value: PeriodicityTypes }[] = [
  {
    label: 'Often',
    value: PeriodicityTypes.OFTEN,
  },
  {
    label: 'Sometimes',
    value: PeriodicityTypes.SOMETIMES,
  },
  {
    label: 'Never',
    value: PeriodicityTypes.NEVER,
  },
];

function OSNButtonGroup(props: OSNButtonGroupProps) {
  const { productId, onChange: passedOnChange } = props;

  const ref = useRef<HTMLButtonElement | null>(null);
  const [showButtonGroup, setShowButtonGroup] = useState(false);
  const [periodicityValue, setPeriodicityValue] =
    useState<PeriodicityTypes | null>(null);
  const { customerData } = useIdentify();

  const {
    data: productPeriodicityMapData,
    isLoading: productPeriodicityMapIsLoading,
  } = useProductPeriodicityMap();

  const patchCustomerPeriodicities = usePatchCustomerPeriodicities();

  useEffect(() => {
    if (!productPeriodicityMapIsLoading) {
      const periodicityValue = productPeriodicityMapData.get(productId) ?? null;
      const periodicity =
        periodicityValue === PeriodicityTypes.UNSET ? null : periodicityValue;

      setPeriodicityValue(periodicity);
      setShowButtonGroup(periodicity === null);
    }
  }, [productId, productPeriodicityMapData, productPeriodicityMapIsLoading]);

  const onChange = ({
    prevValue,
    newValue,
  }: {
    prevValue: PeriodicityTypes | null;
    newValue: PeriodicityTypes | null;
  }) => {
    if (newValue === null) {
      return;
    }

    setShowButtonGroup(false);
    setPeriodicityValue(newValue);

    if (customerData) {
      patchCustomerPeriodicities.mutate(
        {
          customerId: customerData.id,
          data: {
            periodicity: newValue,
            product: productId,
          },
        },
        {
          onError: () => {
            setPeriodicityValue(prevValue);
            passedOnChange?.({ option: prevValue });
          },
        }
      );
    }
  };

  const { buttonProps } = useButton(
    {
      onPress: () => setShowButtonGroup(true),
    },
    ref
  );

  if (showButtonGroup) {
    return (
      <ButtonsGroup
        className={styles['osn-button-group']}
        handleClick={(option) => {
          onChange({ prevValue: periodicityValue, newValue: option });
          passedOnChange?.({ option });
        }}
        selected={periodicityValue}
        options={periodicityOptions}
        labelVariant="H7"
      />
    );
  }

  return (
    <button
      {...buttonProps}
      data-testid={`osn-single-button-${periodicityOptions.find(
        (option) => option.value === periodicityValue
      )?.label}`}
      ref={ref}
      className={styles['osn-single-button']}
    >
      <Frequency aria-hidden className={styles['osn-single-button-icon']} />
      <Heading variant="H7" className={styles['osn-single-button-text']}>
        {
          periodicityOptions.find((option) => option.value === periodicityValue)
            ?.label
        }
      </Heading>
    </button>
  );
}
